/**
 * Waits until a turn that runs on the server — but not in this tab — has finished.
 *
 * A chat answer or a process step (tender analysis, concept) keeps running on the server when the
 * browser connection drops. After a reload the tab has no stream to re-attach to, so it asks the
 * server (`GET /:customer/conversations/:id/run`) and, while a turn is running, shows the progress
 * dots and polls until it has finished; the caller then reloads the conversation, which by then
 * holds the answer. Measured on Prod (30.09.2026): a tender analysis ran five minutes while the
 * reloaded tab showed only the question.
 */

export type RunState = { running: boolean };

export type FollowDeps = {
    checkRun: (signal: AbortSignal) => Promise<RunState>;
    sleep: (ms: number, signal: AbortSignal) => Promise<void>;
    now: () => number;
};

/** Polling intervals: quick at first (a short answer), then settling at 10 s. */
export const FOLLOW_INTERVALS_MS = [2000, 3000, 5000, 8000, 10000];

/** Matches the server's marker expiry (CONVERSATION_RUN_MAX_MS) — no turn runs longer. */
export const FOLLOW_MAX_MS = 45 * 60_000;

export type FollowOutcome = 'finished' | 'timeout' | 'aborted';

export async function waitForRunToFinish(
    deps: FollowDeps,
    signal: AbortSignal,
    maxMs: number = FOLLOW_MAX_MS
): Promise<FollowOutcome> {
    const start = deps.now();
    for (let attempt = 0; ; attempt++) {
        if (signal.aborted) return 'aborted';
        if (deps.now() - start >= maxMs) return 'timeout';
        const interval = FOLLOW_INTERVALS_MS[Math.min(attempt, FOLLOW_INTERVALS_MS.length - 1)];
        try {
            await deps.sleep(interval, signal);
        } catch {
            return 'aborted';
        }
        if (signal.aborted) return 'aborted';
        try {
            const state = await deps.checkRun(signal);
            if (!state.running) return 'finished';
        } catch {
            if (signal.aborted) return 'aborted';
            // A failed check (network blip) is not an answer — keep waiting.
        }
    }
}

/** `setTimeout` that rejects when the signal aborts. */
export const abortableSleep = (ms: number, signal: AbortSignal): Promise<void> =>
    new Promise((resolve, reject) => {
        if (signal.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));
            return;
        }
        const timer = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        const onAbort = () => {
            clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
        };
        signal.addEventListener('abort', onAbort, { once: true });
    });
